import { forwardRef, useId, useState } from "react";
import { X } from "@phosphor-icons/react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { clsx } from "clsx";
import { twRing, twTransform } from "@/lib/emotionTw";
import { Spinner } from "@/components/ui/feedback/Spinner";
import type { InputProps, InputSize } from "./Input.types";

type InputFieldState = "default" | "error";

interface InputOwnerState {
  size: InputSize;
  state: InputFieldState;
  hasLeftIcon: boolean;
  hasRightControl: boolean;
  hasAddons: boolean;
  disabled: boolean;
  fullWidth: boolean;
}

/* Styles are the exact compiled output of the pre-migration Tailwind utilities
 * (extracted from the frozen baseline stylesheet), preserved rule-for-rule in
 * cva/tailwind-merge emission order. Parity is verified computed-property-by-
 * property against the frozen baseline build — do not "clean up" values. */

const filledSelector = "&[data-filled]";

const standaloneBase: CSSObject = {
  width: "100%",
  borderRadius: "var(--bfrs-input-radius)",
  borderWidth: "1px",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
  outline: "2px solid transparent",
  outlineOffset: "2px",
  transitionProperty:
    "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
  transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
  transitionDuration: ".15s",
  "&::-moz-placeholder": {
    "--tw-text-opacity": "1",
    color: "rgb(var(--bfrs-color-text-placeholder) / var(--tw-text-opacity, 1))"
  },
  "&::placeholder": {
    "--tw-text-opacity": "1",
    color: "rgb(var(--bfrs-color-text-placeholder) / var(--tw-text-opacity, 1))"
  },
  "&:disabled::-moz-placeholder": {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
  },
  "&:disabled::placeholder": {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
  },
  "&:focus": {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))",
    ...twRing("2px", "hsl(var(--bfrs-color-primary-soft) / var(--tw-ring-opacity, 1))"),
    "--tw-ring-opacity": "1"
  },
  "&:focus-visible": {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))",
    ...twRing("2px", "hsl(var(--bfrs-color-primary-soft) / var(--tw-ring-opacity, 1))"),
    "--tw-ring-opacity": "1"
  },
  "&:disabled": {
    cursor: "not-allowed",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-app) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
  },
  "&:-moz-read-only": {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-app) / var(--tw-bg-opacity, 1))"
  },
  "&:read-only": {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-app) / var(--tw-bg-opacity, 1))"
  }
};

const standaloneSize: Record<InputSize, CSSObject> = {
  xs: { height: "1.75rem", paddingLeft: ".75rem", paddingRight: ".75rem", fontSize: "12px" },
  sm: { height: "1.75rem", paddingLeft: ".75rem", paddingRight: ".75rem", fontSize: "12px" },
  md: { height: "2.25rem", paddingLeft: ".75rem", paddingRight: ".75rem", fontSize: "12px" },
  lg: { height: "2.25rem", paddingLeft: "1rem", paddingRight: "1rem", fontSize: "12px" }
};

const standaloneState: Record<InputFieldState, CSSObject> = {
  default: {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))"
  },
  error: {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))",
    "&:focus": {
      borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))",
      "--tw-ring-color": "hsl(var(--bfrs-color-danger) / .15)"
    },
    "&:focus-visible": {
      borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))",
      "--tw-ring-color": "hsl(var(--bfrs-color-danger) / .15)"
    }
  }
};

const standaloneLeftIconPad: Record<InputSize, CSSObject> = {
  xs: { paddingLeft: "2rem" },
  sm: { paddingLeft: "2rem" },
  md: { paddingLeft: "2.25rem" },
  lg: { paddingLeft: "2.5rem" }
};

const standaloneRightControlPad: Record<InputSize, CSSObject> = {
  xs: { paddingRight: "2rem" },
  sm: { paddingRight: "2rem" },
  md: { paddingRight: "2.25rem" },
  lg: { paddingRight: "2.5rem" }
};

const smFilledBorder: CSSObject = {
  [filledSelector]: {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))"
  }
};

const addonInnerInput: CSSObject = {
  height: "100%",
  width: "100%",
  minWidth: "0px",
  WebkitAppearance: "none",
  MozAppearance: "none",
  appearance: "none",
  borderWidth: "0px",
  backgroundColor: "transparent",
  paddingLeft: "10px",
  paddingRight: "10px",
  color: "inherit",
  outline: "2px solid transparent",
  outlineOffset: "2px",
  "&::-moz-placeholder": {
    "--tw-text-opacity": "1",
    color: "rgb(var(--bfrs-color-text-placeholder) / var(--tw-text-opacity, 1))"
  },
  "&::placeholder": {
    "--tw-text-opacity": "1",
    color: "rgb(var(--bfrs-color-text-placeholder) / var(--tw-text-opacity, 1))"
  },
  "&:disabled::-moz-placeholder": {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
  },
  "&:disabled::placeholder": {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
  },
  "&:disabled": {
    cursor: "not-allowed",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
  }
};

const InputElement = styled("input", {
  label: "AgenticInput-input",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: InputOwnerState }>(({ ownerState }) =>
  ownerState.hasAddons
    ? [
        addonInnerInput,
        ownerState.hasLeftIcon && { paddingLeft: "2.25rem" },
        ownerState.hasRightControl && { paddingRight: "2.25rem" }
      ]
    : [
        standaloneBase,
        standaloneSize[ownerState.size],
        standaloneState[ownerState.state],
        ownerState.hasLeftIcon && standaloneLeftIconPad[ownerState.size],
        ownerState.hasRightControl && standaloneRightControlPad[ownerState.size],
        ownerState.size === "sm" && smFilledBorder
      ]
);

const chromeBase: CSSObject = {
  display: "flex",
  width: "100%",
  overflow: "hidden",
  borderRadius: "var(--bfrs-input-radius)",
  borderWidth: "1px",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
  transitionProperty:
    "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
  transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
  transitionDuration: ".15s",
  "&:focus-within": {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))",
    ...twRing("2px", "hsl(var(--bfrs-color-primary-soft) / var(--tw-ring-opacity, 1))"),
    "--tw-ring-opacity": "1"
  }
};

const chromeSize: Record<InputSize, CSSObject> = {
  xs: { height: "1.75rem", fontSize: "12px" },
  sm: { height: "1.75rem", fontSize: "12px" },
  md: { height: "2.25rem", fontSize: "12px" },
  lg: { height: "2.25rem", fontSize: "12px" }
};

const chromeState: Record<InputFieldState, CSSObject> = {
  default: {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))"
  },
  error: {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))",
    "&:focus-within": {
      borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))",
      "--tw-ring-color": "hsl(var(--bfrs-color-danger) / .15)"
    }
  }
};

const chromeDisabled: CSSObject = {
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-app) / var(--tw-bg-opacity, 1))",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
};

const InputChrome = styled("div", {
  label: "AgenticInput-chrome",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: InputOwnerState }>(({ ownerState }) => [
  chromeBase,
  chromeSize[ownerState.size],
  chromeState[ownerState.state],
  ownerState.disabled && chromeDisabled,
  ownerState.size === "sm" && smFilledBorder
]);

const addonSizeStyles: Record<InputSize, CSSObject> = {
  xs: { paddingLeft: ".625rem", paddingRight: ".625rem", fontSize: "11.5px" },
  sm: { paddingLeft: ".625rem", paddingRight: ".625rem", fontSize: "12px" },
  md: { paddingLeft: ".75rem", paddingRight: ".75rem", fontSize: "12.5px" },
  lg: { paddingLeft: ".875rem", paddingRight: ".875rem", fontSize: ".875rem", lineHeight: "1.25rem" }
};

const InputAddon = styled("span", {
  label: "AgenticInput-addon",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { side: "prefix" | "suffix"; size: InputSize } }>(({ ownerState }) => [
  {
    display: "flex",
    alignItems: "center",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))",
    fontWeight: 600,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  },
  ownerState.side === "prefix" ? { borderRightWidth: "1px" } : { borderLeftWidth: "1px" },
  addonSizeStyles[ownerState.size]
]);

const InputField = styled("div", {
  label: "AgenticInput-field",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { hasAddons: boolean } }>(({ ownerState }) => [
  { position: "relative" },
  ownerState.hasAddons && { minWidth: "0px", flex: "1 1 0%" }
]);

const iconBase: CSSObject = {
  pointerEvents: "none",
  position: "absolute",
  top: "50%",
  display: "flex",
  ...twTransform({ "--tw-translate-y": "-50%" }),
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
};

const InputLeftIcon = styled("span", {
  label: "AgenticInput-leftIcon"
})([iconBase, { left: ".75rem" }]);

const InputRightIcon = styled("span", {
  label: "AgenticInput-rightIcon"
})([iconBase, { right: ".75rem" }]);

const InputLoadingIcon = styled("span", {
  label: "AgenticInput-loadingIcon"
})([iconBase, { right: ".75rem" }]);

const InputClearButton = styled("button", {
  label: "AgenticInput-clearButton"
})({
  position: "absolute",
  right: ".5rem",
  top: "50%",
  display: "flex",
  width: "1.25rem",
  height: "1.25rem",
  ...twTransform({ "--tw-translate-y": "-50%" }),
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "var(--bfrs-radius-sm)",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))",
  outline: "2px solid transparent",
  outlineOffset: "2px",
  "&:hover": {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  },
  "&:focus-visible": {
    ...twRing("2px", "hsl(var(--bfrs-color-border-focus) / var(--tw-ring-opacity, 1))"),
    "--tw-ring-opacity": "1"
  }
});

const InputRoot = styled("div", {
  label: "AgenticInput-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: Pick<InputOwnerState, "fullWidth"> }>(({ ownerState }) =>
  ownerState.fullWidth ? { width: "100%" } : { display: "inline-block" }
);

const InputLabel = styled("label", {
  label: "AgenticInput-label"
})({
  marginBottom: ".375rem",
  display: "block",
  fontSize: "12px",
  fontWeight: 600,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
});

const InputRequiredMark = styled("span", {
  label: "AgenticInput-requiredMark"
})({
  marginLeft: ".25rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-danger) / var(--tw-text-opacity, 1))"
});

const helperBase: CSSObject = {
  marginTop: ".375rem",
  fontSize: ".75rem",
  lineHeight: "1.25rem"
};

const InputErrorText = styled("p", {
  label: "AgenticInput-errorText"
})([
  helperBase,
  {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-danger) / var(--tw-text-opacity, 1))"
  }
]);

const InputHelperText = styled("p", {
  label: "AgenticInput-helperText"
})([
  helperBase,
  {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
  }
]);

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      containerClassName,
      labelClassName,
      inputClassName,
      size = "md",
      error,
      errorMessage,
      helperText,
      label,
      required,
      prefix,
      suffix,
      leftIcon,
      rightIcon,
      loading,
      onClear,
      clearLabel = "Clear input",
      fullWidth = true,
      id,
      value,
      defaultValue,
      onChange,
      disabled,
      readOnly,
      "aria-invalid": ariaInvalid,
      "aria-describedby": ariaDescribedBy,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const helperId = helperText ? `${inputId}-helper` : undefined;
    const errorId = errorMessage ? `${inputId}-error` : undefined;
    const describedBy = [ariaDescribedBy, helperId, errorId].filter(Boolean).join(" ") || undefined;
    const isControlled = value !== undefined;
    const [uncontrolledHasValue, setUncontrolledHasValue] = useState(() => {
      const v = defaultValue;
      return v !== undefined && v !== null && String(v).length > 0;
    });
    const hasValue = isControlled
      ? value !== null && String(value).length > 0
      : uncontrolledHasValue;
    const canClear = Boolean(onClear && hasValue && !disabled && !readOnly);
    const hasRightControl = Boolean(rightIcon || canClear || loading);
    const hasAddons = Boolean(prefix || suffix);
    const inputState: InputFieldState = error || errorMessage ? "error" : "default";
    const ownerState: InputOwnerState = {
      size,
      state: inputState,
      hasLeftIcon: Boolean(leftIcon),
      hasRightControl,
      hasAddons,
      disabled: Boolean(disabled),
      fullWidth: Boolean(fullWidth)
    };
    const nativeInput = (
      <InputElement
        ref={ref}
        id={inputId}
        value={value}
        defaultValue={defaultValue}
        disabled={disabled}
        readOnly={readOnly}
        aria-invalid={ariaInvalid ?? error ?? Boolean(errorMessage)}
        aria-describedby={describedBy}
        onChange={(e) => {
          if (!isControlled) setUncontrolledHasValue(e.target.value.length > 0);
          onChange?.(e);
        }}
        data-filled={!hasAddons && hasValue && inputState === "default" ? "" : undefined}
        ownerState={ownerState}
        className={inputClassName}
        {...props}
      />
    );

    return (
      <InputRoot ownerState={ownerState} className={clsx(containerClassName, className)}>
        {label && (
          <InputLabel htmlFor={inputId} className={labelClassName}>
            {label}
            {required && <InputRequiredMark>*</InputRequiredMark>}
          </InputLabel>
        )}
        {hasAddons ? (
          <InputChrome data-filled={hasValue && inputState === "default" ? "" : undefined} ownerState={ownerState}>
            {prefix && <InputAddon ownerState={{ side: "prefix", size }}>{prefix}</InputAddon>}
            <InputField ownerState={{ hasAddons: true }}>
              {leftIcon && <InputLeftIcon>{leftIcon}</InputLeftIcon>}
              {nativeInput}
              {loading && (
                <InputLoadingIcon>
                  <Spinner size="sm" />
                </InputLoadingIcon>
              )}
              {rightIcon && !canClear && !loading && <InputRightIcon>{rightIcon}</InputRightIcon>}
              {canClear && (
                <InputClearButton type="button" aria-label={clearLabel} onClick={onClear}>
                  <X size={12} weight="bold" />
                </InputClearButton>
              )}
            </InputField>
            {suffix && <InputAddon ownerState={{ side: "suffix", size }}>{suffix}</InputAddon>}
          </InputChrome>
        ) : (
          <InputField ownerState={{ hasAddons: false }}>
            {leftIcon && <InputLeftIcon>{leftIcon}</InputLeftIcon>}
            {nativeInput}
            {loading && (
              <InputLoadingIcon>
                <Spinner size="sm" />
              </InputLoadingIcon>
            )}
            {rightIcon && !canClear && !loading && <InputRightIcon>{rightIcon}</InputRightIcon>}
            {canClear && (
              <InputClearButton type="button" aria-label={clearLabel} onClick={onClear}>
                <X size={12} weight="bold" />
              </InputClearButton>
            )}
          </InputField>
        )}
        {errorMessage ? (
          <InputErrorText id={errorId}>{errorMessage}</InputErrorText>
        ) : helperText ? (
          <InputHelperText id={helperId}>{helperText}</InputHelperText>
        ) : null}
      </InputRoot>
    );
  }
);

Input.displayName = "Input";
